import type { FC } from 'react';

import { ConditionalVisibility } from '@eneco/custom-components/pagination';
import { Link, useRouter } from '@eneco/next';
import { Box, IconButton, Stack, Stretch, Text } from '@eneco/sparky';
import { ChevronLeftIcon, ChevronRightIcon } from '@eneco/sparky/icons';

import { getControls } from './getControls';
import { indexSearchParam } from '../UsePagination';

type Props = {
  index: number;
  indexLimit?: number;
  nextButtonLabel: string;
  previousButtonLabel: string;
  navAriaLabel: string;
  pageLinkLabel: string;
};

const Ellipses = () => (
  <Text as="li" aria-hidden="true">
    ...
  </Text>
);

export const Controls: FC<Props> = ({
  index: currentIndex,
  indexLimit,
  nextButtonLabel,
  previousButtonLabel,
  pageLinkLabel,
  navAriaLabel,
}) => {
  const { activePath } = useRouter();

  if (!indexLimit || indexLimit < 1) return null;

  const userFacingIndex = currentIndex + 1;

  const showPreviousButton = currentIndex > 0;
  const showNextButton = currentIndex < indexLimit;

  /**
   * We cannot use the normal searchParams object to update the index,
   * as it only works with absolute URLs, which breaks client-side routing.
   */
  function createUrl(index: number) {
    return `${activePath}?${indexSearchParam}=${index}`;
  }

  return (
    <Stretch>
      <Box paddingY={16} as="nav" aria-label={navAriaLabel}>
        <Stack gap="1" alignX="center" alignY="center" direction="row" as="ul">
          <ConditionalVisibility isVisible={showPreviousButton}>
            <li>
              <Link href={createUrl(userFacingIndex - 1)} linkType="internal">
                <IconButton label={previousButtonLabel}>
                  <ChevronLeftIcon />
                </IconButton>
              </Link>
            </li>
          </ConditionalVisibility>
          {getControls(indexLimit, currentIndex).map(controlItem => {
            if (typeof controlItem === 'string') {
              return <Ellipses key={controlItem} />;
            }
            const hiddenLabel = pageLinkLabel.replace('{index}', controlItem.toString());
            return (
              <li key={controlItem}>
                <Link href={createUrl(controlItem)} linkType="internal">
                  <IconButton label={hiddenLabel} isCurrent={controlItem === userFacingIndex}>
                    {controlItem}
                  </IconButton>
                </Link>
              </li>
            );
          })}
          <ConditionalVisibility isVisible={showNextButton}>
            <li>
              <Link href={createUrl(userFacingIndex + 1)} linkType="internal">
                <IconButton label={nextButtonLabel}>
                  <ChevronRightIcon />
                </IconButton>
              </Link>
            </li>
          </ConditionalVisibility>
        </Stack>
      </Box>
    </Stretch>
  );
};
